import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdminDashboardPage = () => {
  const { getToken } = useAuth();
  const [metrics, setMetrics] = useState({
    totalLeads: 0,
    newLeadsThisWeek: 0,
    newLeadsThisMonth: 0,
    conversionRate: 0,
    recentLeads: [],
    leadsBySource: [],
    leadsTrend: []
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/metrics`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-nunito text-white">Dashboard</h1>
        <p className="mt-1 text-orange-300 font-nunito">Welcome back! Here's your overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-300 font-nunito">Total Leads</p>
              <p className="mt-2 text-3xl font-bold text-white font-nunito">{metrics.totalLeads}</p>
            </div>
            <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
          </div>
        </div>

        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-300 font-nunito">This Week</p>
              <p className="mt-2 text-3xl font-bold text-white font-nunito">{metrics.newLeadsThisWeek}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">📈</span>
            </div>
          </div>
        </div>

        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-300 font-nunito">This Month</p>
              <p className="mt-2 text-3xl font-bold text-white font-nunito">{metrics.newLeadsThisMonth}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </div>

        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-300 font-nunito">Conversion Rate</p>
              <p className="mt-2 text-3xl font-bold text-white font-nunito">{metrics.conversionRate}%</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads Trend Chart */}
        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold font-nunito text-white mb-4">Leads Trend (Last 30 Days)</h2>
          {metrics.leadsTrend && metrics.leadsTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={metrics.leadsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="#fed7aa"
                  tick={{ fill: '#fed7aa', fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#fed7aa" tick={{ fill: '#fed7aa', fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#451a03', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  labelStyle={{ color: '#fed7aa' }}
                  itemStyle={{ color: '#f59e0b' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#f59e0b" 
                  strokeWidth={3}
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Leads"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-orange-400 font-nunito">
              No data available
            </div>
          )}
        </div>

        {/* Leads by Status Chart */}
        <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold font-nunito text-white mb-4">Leads by Status</h2>
          {metrics.leadsByStatus && metrics.leadsByStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metrics.leadsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ status, percent }) => `${status}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {metrics.leadsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={[
                      '#10b981', // green for new
                      '#f59e0b', // amber for contacted
                      '#3b82f6', // blue for qualified
                      '#8b5cf6', // purple for converted
                      '#ef4444'  // red for lost
                    ][index % 5]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#451a03', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  labelStyle={{ color: '#fed7aa' }}
                  itemStyle={{ color: '#f59e0b' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-orange-400 font-nunito">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold font-nunito text-white">Recent Leads</h2>
          <Link
            to="/admin/leads"
            className="text-sm text-amber-300 hover:text-amber-400 transition font-nunito"
          >
            View All →
          </Link>
        </div>

        {metrics.recentLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-orange-200 font-nunito">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-orange-200 font-nunito">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-orange-200 font-nunito">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-orange-200 font-nunito">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-orange-200 font-nunito">Status</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recentLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5 transition">
                    <td className="py-3 px-4 text-white font-nunito">{lead.name}</td>
                    <td className="py-3 px-4 text-orange-300 font-nunito">{lead.email}</td>
                    <td className="py-3 px-4 text-orange-300 font-nunito">{lead.company || '-'}</td>
                    <td className="py-3 px-4 text-orange-300 font-nunito">
                      {new Date(lead.submittedAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-300 font-nunito">
                        New
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-orange-400 font-nunito">No leads yet</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/leads"
          className="bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 rounded-2xl p-6 hover:scale-105 transition"
        >
          <span className="text-3xl">👥</span>
          <h3 className="mt-3 text-lg font-bold text-white font-nunito">Manage Leads</h3>
          <p className="mt-1 text-sm text-orange-300 font-nunito">View and manage all your leads</p>
        </Link>

        <Link
          to="/admin/blog"
          className="bg-gradient-to-br from-blue-500/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-6 hover:scale-105 transition"
        >
          <span className="text-3xl">📝</span>
          <h3 className="mt-3 text-lg font-bold text-white font-nunito">Write Blog Post</h3>
          <p className="mt-1 text-sm text-orange-300 font-nunito">Create and publish new content</p>
        </Link>

        <Link
          to="/admin/newsletter"
          className="bg-gradient-to-br from-green-500/20 to-teal-600/20 border border-green-500/30 rounded-2xl p-6 hover:scale-105 transition"
        >
          <span className="text-3xl">📧</span>
          <h3 className="mt-3 text-lg font-bold text-white font-nunito">Send Newsletter</h3>
          <p className="mt-1 text-sm text-orange-300 font-nunito">Email updates to your contacts</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
